import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Cpu,
  Search,
  RefreshCw,
  ChevronRight,
  Shield,
  MapPin,
  Clock,
  Activity,
  Wifi,
  Bot,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Nodes = () => {
  const location = useLocation();
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const isDemo = new URLSearchParams(location.search).get("demo") === "true";

  useEffect(() => {
    fetchNodes();
  }, [isDemo]);

  const fetchNodes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/nodes${isDemo ? '?demo=true' : ''}`, {
        credentials: "include"
      });
      const data = await res.json();
      setNodes(data);
    } catch (e) {
      console.error("Failed to fetch nodes:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = async (node) => {
    setSelectedNode(node);
    setDetailOpen(true);
  };

  const filteredNodes = nodes.filter(node =>
    node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    node.node_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    node.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "online": return "bg-[#00FF94]";
      case "offline": return "bg-red-400";
      case "syncing": return "bg-yellow-400";
      case "maintenance": return "bg-[#00D2FF]";
      default: return "bg-gray-400";
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      online: "bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20",
      offline: "bg-red-500/10 text-red-400 border-red-500/20",
      syncing: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      maintenance: "bg-[#00D2FF]/10 text-[#00D2FF] border-[#00D2FF]/20"
    };
    return colors[status] || "bg-gray-500/10 text-gray-400 border-gray-500/20";
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 60000);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="nodes-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48 bg-white/5" />
          <Skeleton className="h-10 w-64 bg-white/5" />
        </div>
        <Skeleton className="h-96 bg-white/5" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nodes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white">Nodes</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage your fleet of {nodes.length} devices
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search nodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64 bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-500"
              data-testid="search-nodes"
            />
          </div>
          <Button 
            variant="outline" 
            size="icon"
            onClick={fetchNodes}
            className="border-white/10 hover:bg-white/5"
            data-testid="refresh-nodes"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 flex items-center justify-center">
                <Cpu className="w-5 h-5 text-[#00FF94]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white font-mono">
                  {nodes.filter(n => n.status === "online").length}
                </p>
                <p className="text-xs text-gray-500">Online</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Activity className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white font-mono">
                  {nodes.filter(n => n.status === "syncing").length}
                </p>
                <p className="text-xs text-gray-500">Syncing</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00D2FF]/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#00D2FF]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white font-mono">
                  {nodes.filter(n => n.enclave_state === "secure").length}
                </p>
                <p className="text-xs text-gray-500">Secure Enclaves</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#3399FF]/10 flex items-center justify-center">
                <Wifi className="w-5 h-5 text-[#3399FF]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white font-mono">
                  {(nodes.reduce((acc, n) => acc + n.bandwidth, 0) / nodes.length || 0).toFixed(0)}
                </p>
                <p className="text-xs text-gray-500">Avg Bandwidth (MB/s)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Nodes table */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-gray-500">Status</TableHead>
                <TableHead className="text-gray-500">Node</TableHead>
                <TableHead className="text-gray-500">Type</TableHead>
                <TableHead className="text-gray-500">Enclave</TableHead>
                <TableHead className="text-gray-500">Location</TableHead>
                <TableHead className="text-gray-500">Last Sync</TableHead>
                <TableHead className="text-gray-500">Bandwidth</TableHead>
                <TableHead className="text-gray-500">Trust</TableHead>
                <TableHead className="text-gray-500"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredNodes.map((node) => (
                <TableRow 
                  key={node.node_id}
                  className="border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                  onClick={() => handleNodeClick(node)}
                  data-testid={`node-row-${node.node_id}`}
                >
                  <TableCell>
                    <span className={`w-2.5 h-2.5 rounded-full inline-block ${getStatusColor(node.status)} ${node.status === 'syncing' ? 'animate-pulse' : ''}`} />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-white">{node.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{node.node_id}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-white/10 text-gray-400 font-mono text-xs">
                      {node.hardware_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={node.enclave_state === "secure" 
                      ? "bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20" 
                      : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                    }>
                      {node.enclave_state}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-400">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {node.location}
                    </span>
                  </TableCell>
                  <TableCell className="text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(node.last_sync)}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-white">
                    {node.bandwidth.toFixed(1)} MB/s
                  </TableCell>
                  <TableCell>
                    <span className={`font-mono ${
                      node.trust_score >= 95 ? 'text-[#00FF94]' :
                      node.trust_score >= 80 ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>
                      {node.trust_score.toFixed(1)}%
                    </span>
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Node detail sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-[#0A0A0A] border-white/10">
          <SheetHeader>
            <SheetTitle className="font-heading text-white flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(selectedNode?.status)}`} />
              {selectedNode?.name}
            </SheetTitle>
          </SheetHeader>
          
          {selectedNode && (
            <div className="mt-6 space-y-6">
              {/* Basic info */}
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-500 text-sm">Node ID</span>
                  <span className="font-mono text-[#00D2FF] text-sm">{selectedNode.node_id}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-500 text-sm">Status</span>
                  <Badge className={getStatusBadge(selectedNode.status)}>
                    {selectedNode.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-500 text-sm">Hardware</span>
                  <span className="text-white text-sm">{selectedNode.hardware_type}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-500 text-sm">IP Address</span>
                  <span className="font-mono text-white text-sm">{selectedNode.ip_address}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-500 text-sm">Firmware</span>
                  <span className="font-mono text-white text-sm">{selectedNode.firmware_version}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-500 text-sm">Enclave State</span>
                  <Badge className={selectedNode.enclave_state === "secure" 
                    ? "bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20" 
                    : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                  }>
                    <Shield className="w-3 h-3 mr-1" />
                    {selectedNode.enclave_state}
                  </Badge>
                </div>
              </div>

              {/* Telemetry */}
              <div>
                <h3 className="font-heading text-white font-semibold mb-3">Live Telemetry</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="glass rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Bandwidth</p>
                    <p className="text-lg font-mono text-white">{selectedNode.bandwidth.toFixed(1)} MB/s</p>
                  </div>
                  <div className="glass rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Trust Score</p>
                    <p className={`text-lg font-mono ${
                      selectedNode.trust_score >= 95 ? 'text-[#00FF94]' :
                      selectedNode.trust_score >= 80 ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>{selectedNode.trust_score.toFixed(1)}%</p>
                  </div>
                </div>
              </div>

              {/* Active agents */}
              <div>
                <h3 className="font-heading text-white font-semibold mb-3">Active Agents</h3>
                <div className="space-y-2">
                  {selectedNode.active_agents?.map((agent) => (
                    <div key={agent} className="flex items-center gap-2 py-2 px-3 glass rounded-lg">
                      <Bot className="w-4 h-4 text-[#00D2FF]" />
                      <span className="text-sm text-white">{agent}</span>
                      <Badge className="ml-auto bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20 text-xs">
                        Running
                      </Badge>
                    </div>
                  ))}
                  {(!selectedNode.active_agents || selectedNode.active_agents.length === 0) && (
                    <p className="text-gray-500 text-sm">No active agents</p>
                  )}
                </div>
              </div>

              {/* Camera hash preview placeholder */}
              <div>
                <h3 className="font-heading text-white font-semibold mb-3">Sensor Data Hashes</h3>
                <div className="glass rounded-lg p-4 font-mono text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Camera L</span>
                    <span className="text-[#00D2FF]">0x{Math.random().toString(16).slice(2, 10)}...</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Camera R</span>
                    <span className="text-[#00D2FF]">0x{Math.random().toString(16).slice(2, 10)}...</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">LiDAR</span>
                    <span className="text-[#00D2FF]">0x{Math.random().toString(16).slice(2, 10)}...</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">IMU</span>
                    <span className="text-[#00D2FF]">0x{Math.random().toString(16).slice(2, 10)}...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Nodes;
